"use client";

import React from "react";
import { PlayerSeek, PlayerTime } from "@/components/ui/audiocn/player";
import { TPlay } from "@/components/ui/tls/t-play";
import type { IntentPreloadHandlers } from "@/hooks/useIntentPreload";

export interface WaveformBandProps {
  /** Stored waveform_data peaks. Never decoded from audio. */
  peaks: ArrayLike<number> | null | undefined;
  /** Playback position in seconds; read only while this row is active. */
  currentTime: number;
  /** Length in seconds the strip and readout run to. */
  duration: number;
  isPlaying: boolean;
  isLoading: boolean;
  /** Sound title, for the stable "Play <title>" and "Seek <title>" names. */
  title: string;
  onPlayPause: (e?: React.MouseEvent) => void;
  onSeek?: (time: number) => void;
  /**
   * False keeps the strip a drawing with no slider, e.g. while the row-error
   * store holds a failure for this sound and a seek could do nothing.
   */
  seekable?: boolean;
  /**
   * This row's sound is the current one, so the row is the expanded plate:
   * the strip, readout and seek slider exist only then.
   */
  expanded?: boolean;
  /** Pointer handlers that buffer this row's audio on hover/press intent. */
  intentHandlers?: IntentPreloadHandlers;
}

/**
 * The row's transport: the AudioCN play disc, and on the active row the
 * AudioCN seek strip with its time readout.
 *
 * A resting row draws no strip at all (the row CSS has never shown one), so a
 * 24-row list mounts no canvas and runs no frame loop. Only the one active
 * row mounts PlayerSeek, which hands its server-drawn ticks to the canvas
 * after hydration.
 */
export function WaveformBand({
  peaks,
  currentTime,
  duration,
  isPlaying,
  isLoading,
  title,
  onPlayPause,
  onSeek,
  seekable = true,
  expanded = false,
  intentHandlers,
}: WaveformBandProps) {
  return (
    <div className={expanded ? "sc-band is-expanded" : "sc-band"} {...intentHandlers}>
      {/* Play comes first so focus order follows the strip's reading order:
          the disc sits at the strip's left edge, the slider to its right. */}
      <TPlay
        playing={isPlaying || isLoading}
        loading={isLoading}
        ground={expanded ? "inverted" : "canvas"}
        title={title}
        size="css"
        className="sc-play"
        onClick={e => {
          e.stopPropagation();
          onPlayPause();
        }}
      />

      {expanded ? (
        <div className="sc-strip">
          <PlayerSeek
            className="sc-seek h-full"
            peaks={peaks}
            duration={duration}
            currentTime={currentTime}
            title={title}
            // Tenths in the value text, so each 0.1 s arrow press announces a
            // new value; the readout beside it stays in whole seconds.
            decimals={1}
            interactive={seekable && duration > 0}
            onSeekCommit={onSeek}
          />
          <span className="sc-time">
            <PlayerTime inheritFont currentTime={currentTime} duration={duration} />
            {" / "}
            <PlayerTime inheritFont className="min-w-0" type="duration" duration={duration} />
          </span>
        </div>
      ) : null}
    </div>
  );
}
